// Autosave: when the editor buffer is dirty and the user has stopped
// typing for `idleMs`, fire `save`. Setting is persisted in
// localStorage for instant boot; cloud-syncing settings is a later
// step. The "Saved Ns ago" / "Saving…" indicator is exposed so the
// editor chrome can surface it next to the manual Save button.

import { useCallback, useEffect, useRef, useState } from 'react';

const SETTINGS_KEY = 'strudel-harness:settings:v1';

export type AutosaveSettings = {
  enabled: boolean;
  idleMs: number;
};

const DEFAULTS: AutosaveSettings = { enabled: false, idleMs: 2000 };

function readSettings(): AutosaveSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<AutosaveSettings>;
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULTS.enabled,
      idleMs:
        typeof parsed.idleMs === 'number' && parsed.idleMs >= 500
          ? parsed.idleMs
          : DEFAULTS.idleMs,
    };
  } catch {
    return DEFAULTS;
  }
}

function writeSettings(s: AutosaveSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // Best effort.
  }
}

export type AutosaveStatus =
  | { phase: 'idle' }
  | { phase: 'saving' }
  | { phase: 'saved'; at: number };

export function useAutosave(params: {
  dirty: boolean;
  /** Provide the latest title/code so the timer fires the save with
   *  fresh values rather than stale closure captures. */
  title: string;
  code: string;
  save: (title: string, code: string) => void;
}): {
  settings: AutosaveSettings;
  setEnabled: (enabled: boolean) => void;
  setIdleMs: (idleMs: number) => void;
  status: AutosaveStatus;
} {
  const { dirty, title, code, save } = params;
  const [settings, setSettings] = useState<AutosaveSettings>(readSettings);
  const [status, setStatus] = useState<AutosaveStatus>({ phase: 'idle' });

  const titleRef = useRef(title);
  const codeRef = useRef(code);
  titleRef.current = title;
  codeRef.current = code;
  const saveRef = useRef(save);
  saveRef.current = save;

  const setEnabled = useCallback((enabled: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, enabled };
      writeSettings(next);
      return next;
    });
  }, []);

  const setIdleMs = useCallback((idleMs: number) => {
    setSettings((prev) => {
      const clamped = Math.max(500, Math.min(60000, idleMs | 0));
      const next = { ...prev, idleMs: clamped };
      writeSettings(next);
      return next;
    });
  }, []);

  // Debounce: when dirty, schedule a save. Reset on every change.
  useEffect(() => {
    if (!settings.enabled || !dirty) return;
    const timer = window.setTimeout(() => {
      setStatus({ phase: 'saving' });
      try {
        saveRef.current(titleRef.current, codeRef.current);
        setStatus({ phase: 'saved', at: Date.now() });
      } catch (e) {
        // Save threw synchronously — back to idle so the indicator
        // doesn't lie about success. The error surfaces through
        // useLibrary's status/error state.
        setStatus({ phase: 'idle' });
        console.error('[autosave]', e);
      }
    }, settings.idleMs);
    return () => window.clearTimeout(timer);
  }, [dirty, settings.enabled, settings.idleMs, title, code]);

  return { settings, setEnabled, setIdleMs, status };
}
